// ─────────────────────────────────────────────
//  Provider Short Codes — pure domain module (no Firebase)
//  Source: Provider_Code_List.xlsx (AdventHealth Wesley Chapel)
//  Names and codes are extracted from the official roster.
//  IMPORTANT: No phone numbers are stored here — contact data
//  lives only in Firestore user documents.
//
//  Phase 4.1 note: SPARE_CODES and UNCODED_PROVIDERS are exported
//  here for the PDF short-code matcher to consume.
// ─────────────────────────────────────────────

/**
 * Official code → provider name map (54 entries).
 * Names are stored exactly as the xlsx Provider column reads (Last, First format).
 * No phone numbers, no notes — those stay in Firestore only.
 *
 * @type {Record<string, string>}
 */
export const OFFICIAL_DOCTOR_CODES = {
  ALL: "Allee, Joy",
  ATT: "Attaallah, Fikry",
  BEN: "Bennett, Amanda",
  BES: "Best, Dr.",
  BUR: "Burger, Melissa",
  CAR: "Carey, Kyle",
  CHO: "Choi, Diane",
  CMG: "Cummings, Nicholas",
  DAZ: "Dazen, Kevin",
  DPS: "Persaud, Dharam", // unconfirmed per source sheet
  ERB: "Erbaugh, Dr.",
  ESC: "Escobar, Shahla",
  EVA: "Evans, Raymond",
  GAR: "Garcia, Gloria",
  GIB: "Gibson, Dr.",
  HAW: "Hawk, Thomas",
  HOW: "Howell, Franklin",
  HUA: "Huang, Jessica",
  HUM: "Hummel, Jacob",
  IGL: "Iglesias, Dr.",
  INO: "Inouye, John",
  JAC: "Jacob, Salvin",
  JOJ: "Johnson, Jason",
  KIN: "King, Martha Anne",
  KUJ: "Kujak, Mary Kay",
  LB:  "Longbottom, Ward",
  LIP: "Lipska, Anna",
  LUE: "Luebbert, Andrew",
  LUT: "Lutman, Bryan",
  MAH: "Maheshwari, Neeraj",
  MAK: "Mak, Kinlap",
  MAR: "Martinez, Gonzalo",
  MCL: "McCleish, Linda",
  OSU: "Osunsanmi, Babajide (Michael)",
  PAG: "Paganelli, William",
  PAR: "Parvan, Lucia",
  PAT: "Patel, Aalok",
  PEN: "Pena, Dr.",
  PMD: "Pourmand, Eric",
  QUI: "Quick, Johnny",
  RES: "Resnick, Andrew",
  RIV: "Rivera, Peter",
  ROS: "Rosenfeld, Kejia",
  SAN: "Santos, Antonio",
  SER: "Serdiuk, Andrew",
  SHA: "Shah, Mahima",
  TAB: "Tabet, Elias",
  TAN: "Tanner, Leigh",
  VAR: "Varlotta, David",
  WEB: "Webb, Ellen",
  WEI: "Weitzner, Cory",
  WEL: "Wells, Jason",
  WIL: "Wilson, Colin",
  WNR: "Warner, Richard",
};

/**
 * Spare codes reserved in the source sheet — not yet assigned to a provider.
 * Phase 4.1 may assign these via manual Short Code input or PDF matching.
 *
 * @type {string[]}
 */
export const SPARE_CODES = ["BRO", "PAN", "SES", "SHO"];

/**
 * Providers listed in the source sheet with no code assigned yet.
 * Names follow "Last, First" format as in the xlsx.
 * Phase 4.1 may match these via manual input.
 *
 * @type {string[]}
 */
export const UNCODED_PROVIDERS = [
  "Diaz, Antonio",
  "Karim Jetha, Zaheer",
  "Li, Monica",
  "Moayed, Omid",
  "Nguyen, Ngoc",
  "Patel, Lisa",
  "Umholtz, Matthew",
];

// ─────────────────────────────────────────────
//  Name normalization helpers (module-private)
// ─────────────────────────────────────────────

/**
 * Normalize a name string for fuzzy matching:
 * - lowercase
 * - strip leading "Dr." / "Dr " prefix
 * - strip punctuation (commas, periods, parentheses)
 * - collapse whitespace
 * Returns an array of tokens sorted so comparison is order-independent.
 *
 * @param {string} name
 * @returns {string[]} sorted lowercase tokens
 */
function normalizeTokens(name) {
  return name
    .toLowerCase()
    .replace(/^dr\.?\s+/i, "")          // strip "Dr." / "Dr " prefix
    .replace(/[,.()\[\]]/g, " ")         // punctuation → space
    .split(/\s+/)
    .filter(Boolean)
    .sort();
}

/**
 * Convert an xlsx "Last, First" or "Last, First Middle" name to
 * an array of normalized tokens usable for matching against user docs.
 * Also handles "First Last" format (no comma).
 *
 * @param {string} xlsxName
 * @returns {string[]}
 */
function xlsxNameTokens(xlsxName) {
  return normalizeTokens(xlsxName);
}

/**
 * Extract tokens from a user doc name (e.g. "Dr. Leigh Tanner").
 *
 * @param {string} userName
 * @returns {string[]}
 */
function userNameTokens(userName) {
  return normalizeTokens(userName);
}

/**
 * Returns true when two token arrays share at least one last-name candidate
 * AND at least one first-name token — or when the full sorted token sets are
 * identical (covers single-name edge cases).
 *
 * Strategy: we compare sorted token arrays; a match exists when every token in
 * the smaller set appears in the larger set.  This handles:
 * - "Tanner, Leigh" → ["leigh","tanner"] vs "Dr. Leigh Tanner" → ["leigh","tanner"] ✓
 * - "King, Martha Anne" → ["anne","king","martha"] vs "Dr. Martha Anne King" → same ✓
 * - "Karim Jetha, Zaheer" → ["jetha","karim","zaheer"] vs user → same ✓
 * - "Osunsanmi, Babajide (Michael)" → strips parens → ["babajide","michael","osunsanmi"] ✓
 *
 * @param {string[]} aTokens
 * @param {string[]} bTokens
 * @returns {boolean}
 */
function tokensMatch(aTokens, bTokens) {
  if (aTokens.length === 0 || bTokens.length === 0) return false;
  const smaller = aTokens.length <= bTokens.length ? aTokens : bTokens;
  const larger  = aTokens.length <= bTokens.length ? bTokens : aTokens;
  const largerSet = new Set(larger);
  return smaller.every((t) => largerSet.has(t));
}

// ─────────────────────────────────────────────
//  Public matcher
// ─────────────────────────────────────────────

/**
 * Match official codes to user documents.
 *
 * @param {Record<string, string>} codes  - map of code → xlsx provider name
 * @param {Array<{uid: string, name: string, role?: string}>} users
 *   Each user must have `uid` (Firestore doc id) and `name` (display name).
 *   Only users with `role === "anesthesiologist"` are considered; pass a
 *   pre-filtered list or include role on each object.
 *
 * @returns {{
 *   matches:   Array<{uid: string, code: string, name: string}>,
 *   unmatched: Array<{code: string, name: string}>,
 *   ambiguous: Array<{code: string, name: string, candidateUids: string[]}>
 * }}
 */
export function matchCodesToUsers(codes, users) {
  const anesthesiologists = users.filter((u) => !u.role || u.role === "anesthesiologist");

  const matches   = [];
  const unmatched = [];
  const ambiguous = [];

  for (const [code, xlsxName] of Object.entries(codes)) {
    const xlsxTokens = xlsxNameTokens(xlsxName);

    const candidates = anesthesiologists.filter((u) =>
      tokensMatch(xlsxTokens, userNameTokens(u.name || ""))
    );

    if (candidates.length === 0) {
      unmatched.push({ code, name: xlsxName });
    } else if (candidates.length === 1) {
      matches.push({ uid: candidates[0].uid, code, name: xlsxName });
    } else {
      // 2+ users share enough name tokens — never auto-assign
      ambiguous.push({
        code,
        name: xlsxName,
        candidateUids: candidates.map((u) => u.uid),
      });
    }
  }

  return { matches, unmatched, ambiguous };
}
